import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  Dropdown,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Option,
  makeStyles,
} from '@fluentui/react-components';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, Plus, Search, ShieldAlert, Trash2, X } from 'lucide-react';

import { MigrationDevice } from '../api/migrations';
import { normalizeSid } from '../api/identity';
import { pinCanaryFirst } from '../lib/deviceOrder';
import AssignDevicesDialog from '../components/AssignDevicesDialog';
import ConfirmRemoveDevicesModal from '../components/ConfirmRemoveDevicesModal';
import MoveDevicesDialog from '../components/MoveDevicesDialog';
import DeviceGrid, { DeviceGridMappingColumn } from '../components/DeviceGrid';
import ReadinessOverrideDialog from '../components/ReadinessOverrideDialog';
import AutopilotTagSummaryPanel from '../components/AutopilotTagSummaryPanel';
import { MappingChipStatus, deriveMappingChipStatus } from '../components/identity/MappingStatusChip';
import { useIdentitySuggestions, useSuggestionRows } from '../hooks/useIdentitySuggestions';
import { useMigrationDetailQuery } from '../hooks/useMigrationDetail';
import { NUMERIC, STROKE, SURFACE, TEXT } from '../theme/tokens';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: '8px',
    rowGap: '8px',
  },
  search: {
    minWidth: '260px',
    flexGrow: 1,
  },
  filter: {
    minWidth: '170px',
  },
  selectionBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: '8px',
    rowGap: '8px',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '12px',
    paddingRight: '12px',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: STROKE.base,
    borderRightColor: STROKE.base,
    borderBottomColor: STROKE.base,
    borderLeftColor: STROKE.base,
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    backgroundColor: SURFACE.base,
  },
  tableSurface: {
    backgroundColor: SURFACE.base,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: STROKE.base,
    borderRightColor: STROKE.base,
    borderBottomColor: STROKE.base,
    borderLeftColor: STROKE.base,
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    overflowX: 'auto',
  },
});

// FD-372 — the lifecycle-status filter, URL-param driven like `?held=1` (and
// never localStorage, FD-ADR-031 §1): `?status=<key>`. The vocabulary is the
// FD-190 device lifecycle — pending → initialized → in-progress → one of the
// four FA-ADR-021 terminal outcomes — plus one preset:
//
//   `failed` = salvaged + faulted, the H2 retry set (FD-ADR-035). It matches
//   the server's own definition in GET /v1/migrations/{id}/failed-devices:
//   `aborted` is EXCLUDED by design — those devices were deliberately stopped,
//   and sweeping them into a retry set would migrate devices someone chose to
//   stop.
export const LIFECYCLE_STATUS_FILTERS = [
  { key: 'failed', label: 'Failed (salvaged + faulted)' },
  { key: 'pending', label: 'Pending' },
  { key: 'initialized', label: 'Initialized' },
  { key: 'in-progress', label: 'In progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'aborted', label: 'Aborted' },
  { key: 'salvaged', label: 'Salvaged' },
  { key: 'faulted', label: 'Faulted' },
] as const;

// FD-494 — the readiness filter, mockup 2's second dropdown.
//
// IT REUSES `?held=`, THE PARAM FD-287 ALREADY PUT IN THE URL, rather than adding
// a fourth. `?held=1` keeps its exact meaning — the devices readiness WOULD hold —
// so every link an operator has already shared, and the "Show only the N affected"
// button on the panel below, go on working byte-for-byte. The other values are
// per-verdict and new. One param, one meaning ("filter by readiness"), with `1` as
// the legacy spelling of its first option; a second param would be two controls
// that can contradict each other, and a rename would silently break a shared link.
export const READINESS_FILTERS = [
  { key: '1', label: 'Would be held' },
  { key: 'pass', label: 'Ready' },
  { key: 'warn', label: 'Warnings' },
  { key: 'blocked', label: 'Not ready' },
  { key: 'unknown', label: 'Unknown' },
  { key: 'notEvaluated', label: 'Not evaluated' },
] as const;

// FD-494 — the identity filter, `?identity=`. The vocabulary is FD-357's
// `identityStatus`, and the three states stay distinct (the FD-246 discipline):
// `n/a` is an ORDINARY pre-manifest state, not a soft failure. The URL spelling
// is `na` because a slash in a query value is needless encoding noise.
export const IDENTITY_FILTERS = [
  { key: 'ready', label: 'Mapped' },
  { key: 'not-ready', label: 'Waiting for mapping' },
  { key: 'na', label: 'Not applicable' },
] as const;

const ALL_STATUSES_VALUE = '__all__';
const ALL_READINESS_VALUE = '__all_readiness__';
const ALL_IDENTITIES_VALUE = '__all_identities__';

// Status strings arrive in several spellings ("InProgress", "in-progress",
// "in progress" — the StatusBadge/classifyDeviceStatus precedent), so matching
// strips case and separators rather than comparing literals.
function normalizeLifecycleStatus(status: string): string {
  return status.trim().toLowerCase().replace(/[\s_-]+/g, '');
}

export function matchesLifecycleFilter(deviceStatus: string, filterKey: string): boolean {
  const normalized = normalizeLifecycleStatus(deviceStatus);
  if (filterKey === 'failed') {
    return normalized === 'salvaged' || normalized === 'faulted';
  }
  return normalized === normalizeLifecycleStatus(filterKey);
}

export function matchesReadinessFilter(
  device: Pick<MigrationDevice, 'readinessStatus'>,
  filterKey: string,
): boolean {
  const status = device.readinessStatus;
  if (filterKey === '1') {
    // "Would be held" rather than "is held": under `observe` nothing is actually
    // withheld, but these are exactly the devices an operator needs to look at
    // before deciding whether enforcement is safe to turn on. `null` is in the
    // set because a device with no manifest has no verdict to clear the gate
    // with, which is the FD-ADR-027 fail-closed rule.
    return status === 'blocked' || status === 'unknown' || status === null;
  }
  if (filterKey === 'notEvaluated') return status === null;
  return status === filterKey;
}

export function matchesIdentityFilter(
  device: Pick<MigrationDevice, 'identityStatus'>,
  filterKey: string,
): boolean {
  if (filterKey === 'na') return device.identityStatus === 'n/a';
  return device.identityStatus === filterKey;
}

// The serial search. A CONTAINS MATCH OVER THE ROWS ALREADY LOADED, and the copy
// must not imply otherwise.
//
// This is a genuinely different thing from the Resources device search, which is
// a SERVER prefix query with a three-character floor because it scans a partition
// (`DevicesListPage`). Here the campaign's whole device list arrived in one
// partition read on the migration-detail response — there is no page boundary, no
// request per keystroke, and therefore no "of loaded pages" caveat to write and no
// minimum length to enforce. Contains rather than prefix for the same reason: it
// costs nothing and an operator hunting a serial usually remembers the middle.
export function matchesSerialSearch(serialNumber: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return serialNumber.toLowerCase().includes(q);
}

export default function MigrationDevicesPage() {
  const styles = useStyles();
  const { migrationId } = useParams<{ migrationId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useMigrationDetailQuery(migrationId);

  const [assignOpen, setAssignOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  // FD-206 — filter to the devices readiness would hold. Owned here rather than
  // in the panel so the panel and the grid cannot disagree about what is shown.
  //
  // FD-287 moved it from useState INTO THE URL. This is the view an operator
  // shares when asking "why is this wave not ready" — a filter held in component
  // state produces a link that opens on the unfiltered grid, which is a
  // different page from the one they were looking at. Same reason every other
  // filter in this console lives in the URL; never localStorage (FD-ADR-031 §1).
  //
  // FD-494 widened the same param from a boolean to a readiness vocabulary; see
  // READINESS_FILTERS above for why it is not a new param.
  const [searchParams, setSearchParams] = useSearchParams();

  // One writer for all four params, so they compose rather than clobber each
  // other. `replace` throughout so toggling a filter does not stack history
  // entries an operator then has to press Back through.
  const setParam = (name: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(name, value);
    else next.delete(name);
    setSearchParams(next, { replace: true });
  };

  const rawStatusFilter = searchParams.get('status');
  const statusFilter =
    rawStatusFilter && LIFECYCLE_STATUS_FILTERS.some((f) => f.key === rawStatusFilter)
      ? rawStatusFilter
      : null;

  const rawReadinessFilter = searchParams.get('held');
  const readinessFilter =
    rawReadinessFilter && READINESS_FILTERS.some((f) => f.key === rawReadinessFilter)
      ? rawReadinessFilter
      : null;

  const rawIdentityFilter = searchParams.get('identity');
  const identityFilter =
    rawIdentityFilter && IDENTITY_FILTERS.some((f) => f.key === rawIdentityFilter)
      ? rawIdentityFilter
      : null;

  const searchQuery = searchParams.get('q') ?? '';

  // Multi-select state for the device grid. Keys are serial numbers. Cleared on
  // every successful remove so a completed batch doesn't leave stale selections.
  const [selectedSerials, setSelectedSerials] = useState<Set<string>>(new Set());

  // FD-503 — the single device a row `···` action is aimed at, or null when the
  // dialog was opened from the bulk bar. It is NOT a second selection model: it
  // is read only while a dialog is open and cleared as the dialog closes, so the
  // bulk bar's own count and the dialog's subject can never disagree.
  const [rowTarget, setRowTarget] = useState<MigrationDevice | null>(null);

  const devicesForSelection = query.data?.devices ?? [];
  const selectedDevices = useMemo(
    () => devicesForSelection.filter((d) => selectedSerials.has(d.serialNumber)),
    [devicesForSelection, selectedSerials],
  );

  const migrationType = query.data?.migration.migrationType;
  const isMappingType = migrationType === 'T2T' || migrationType === 'D2C';
  const suggestionsQuery = useIdentitySuggestions(migrationId, isMappingType);
  const { bySid: suggestionsBySid } = useSuggestionRows(suggestionsQuery);

  // Device-grid "Mapping" chip derivation (FD-103): null primaryUserSid →
  // No manifest; otherwise join the device SID against the suggestion rows by
  // normalized SID and render the server facts. The chip click now NAVIGATES to
  // the Identities sub-page carrying the target SID in the URL hash (FD-127) —
  // the mapping rows moved off this route, so an in-page scroll no longer works.
  const mappingColumn = useMemo<DeviceGridMappingColumn | undefined>(() => {
    if (!isMappingType) return undefined;
    return {
      statusForDevice: (device: MigrationDevice): MappingChipStatus | null => {
        if (!device.primaryUserSid) return 'noManifest';
        const row = suggestionsBySid.get(normalizeSid(device.primaryUserSid));
        if (row) return deriveMappingChipStatus(row);
        if (suggestionsQuery.isLoading || suggestionsQuery.hasNextPage) return null;
        return suggestionsQuery.isError ? null : 'unmapped';
      },
      onChipClick: (device: MigrationDevice) => {
        if (!device.primaryUserSid || !migrationId) return;
        navigate({
          pathname: `/migrations/${encodeURIComponent(migrationId)}/identities`,
          hash: `#mapping-row-${normalizeSid(device.primaryUserSid)}`,
        });
      },
    };
  }, [
    isMappingType,
    suggestionsBySid,
    suggestionsQuery.isLoading,
    suggestionsQuery.hasNextPage,
    suggestionsQuery.isError,
    migrationId,
    navigate,
  ]);

  const invalidateMigrationQueries = () => {
    // Refetch the current migration detail so the grid picks up the new state,
    // and invalidate the list so deviceCount stays in sync on the parent page.
    queryClient.invalidateQueries({ queryKey: ['migration', migrationId] });
    queryClient.invalidateQueries({ queryKey: ['migrations'] });
  };

  // FD-290 — a row opens the DEVICE, not its event log.
  //
  // FD-283 built the device detail page and its campaign-scoped route
  // (`/migrations/{id}/devices/{serial}`) and never wired this entry point to
  // it, so the route was reachable only by typing a URL. Two symptoms, and the
  // second is the worse one:
  //
  //   - a device WITH a jobId jumped straight past its own page to the raw
  //     event trail, skipping readiness, user identity and lifecycle state;
  //   - a device WITHOUT a jobId did NOTHING AT ALL. The old guard returned
  //     early because an event log with no job really would be empty — but that
  //     turned "this device has not booted yet" into a dead click, which reads
  //     as a broken console. Every device in a freshly assigned campaign is in
  //     that state.
  //
  // The detail page handles the no-jobId case explicitly ("has not booted yet",
  // and it does not fetch a trail that cannot exist), and carries the link on to
  // the event log for devices that have one. So there is no longer any reason to
  // gate the click.
  const handleDeviceClick = (device: MigrationDevice) => {
    if (!migrationId) return;
    navigate(
      `/migrations/${encodeURIComponent(migrationId)}/devices/${encodeURIComponent(
        device.serialNumber,
      )}`,
    );
  };

  if (!query.data || !migrationId) return null;

  const { migration, devices } = query.data;
  const selectedCount = selectedSerials.size;

  // FD-358 / FD-494 — the identity surface is gated on the field actually
  // arriving, so a Manager deployed before FD-357 renders neither an empty column
  // nor a dropdown whose every option would empty the grid.
  const hasIdentityData = devices.some(
    (d) => d.identityStatus !== undefined && d.identityStatus !== null,
  );

  // FD-498 — the Step column, gated the same way and for the same reason. The
  // predicate is `!== undefined` rather than truthiness: `null` means this
  // Manager DOES project the field and this device has never reported progress,
  // which is a real answer the column renders as an em-dash. `undefined` means
  // the Manager predates FD-497 and the question cannot be asked at all, which
  // is a column of em-dashes across every row.
  const hasStepData = devices.some((d) => d.lastActionId !== undefined);

  // Four orthogonal questions, ANDed in one pass: which lifecycle state, which
  // readiness verdict, which identity state, and does the serial match.
  const visibleDevices = devices.filter((d) => {
    if (statusFilter && !matchesLifecycleFilter(d.status, statusFilter)) return false;
    if (readinessFilter && !matchesReadinessFilter(d, readinessFilter)) return false;
    if (identityFilter && !matchesIdentityFilter(d, identityFilter)) return false;
    if (!matchesSerialSearch(d.serialNumber, searchQuery)) return false;
    return true;
  });

  // FD-503 (FD-ADR-035 decision 4) — the canary sorts FIRST, within whatever the
  // filters left. It is the row the entire wave is waiting on: every other device
  // holds at `409 CANARY_HOLD` until it completes, and a canary that salvages or
  // faults keeps the wave held until an operator releases it. That row being
  // twelfth in serial order is the table failing at the moment it matters most.
  //
  // `pinCanaryFirst` NEVER injects — a canary the operator's own filters excluded
  // stays excluded, because the pin is an ordering rule and not a visibility one.
  const orderedDevices = useMemo(
    () => pinCanaryFirst(visibleDevices, migration.canarySerial),
    [visibleDevices, migration.canarySerial],
  );

  // The dialogs' subject: one row when a `···` opened them, otherwise the bulk
  // selection. Computed here so all three read the same value.
  const dialogDevices = rowTarget ? [rowTarget] : selectedDevices;

  // FD-503 — the row `···`. Each item opens the dialog the BULK BAR opens,
  // scoped to one device via `rowTarget`, so there is exactly one implementation
  // of each irreversible act. `rowTarget` is cleared when the dialog closes, so a
  // later bulk action cannot inherit a row's target — the failure this shape has
  // to avoid, and the reason the dialogs read `dialogDevices` rather than
  // `selectedDevices` directly.
  const rowActions = useMemo(
    () => ({
      onOpen: handleDeviceClick,
      onMove: (d: MigrationDevice) => {
        setRowTarget(d);
        setMoveOpen(true);
      },
      onRemove: (d: MigrationDevice) => {
        setRowTarget(d);
        setRemoveOpen(true);
      },
      onOverrideReadiness: (d: MigrationDevice) => {
        setRowTarget(d);
        setOverrideOpen(true);
      },
    }),
    // `handleDeviceClick` is stable for a given migration id, which is what this
    // list is really keyed on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [migrationId],
  );

  const activeStatusFilterLabel =
    LIFECYCLE_STATUS_FILTERS.find((f) => f.key === statusFilter)?.label ?? 'All statuses';
  const activeReadinessFilterLabel =
    READINESS_FILTERS.find((f) => f.key === readinessFilter)?.label ?? 'All readiness';
  const activeIdentityFilterLabel =
    IDENTITY_FILTERS.find((f) => f.key === identityFilter)?.label ?? 'All identities';

  const filtered = visibleDevices.length !== devices.length;

  return (
    <div className={styles.page}>
      {/* FD-503 — THE READINESS TILES AND THE ENFORCEMENT PANEL ARE NOT HERE.
          They moved to Overview, where the campaign's monitoring view already
          lives (owner decision, 2026-09-27). They were a wave-level AGGREGATE
          sitting on top of a per-device TABLE: five numbers and a policy
          paragraph an operator scrolled past to reach the rows they came for,
          on the one tab whose job is the rows.

          WHAT STAYS HERE IS EVERYTHING THAT ACTS PER DEVICE: the Readiness
          column, the readiness filter below, and the bulk override. The held
          filter is still reachable from the panel in its new home — its button
          navigates here with `?held=1`, which is the same param this dropdown
          reads, so the two cannot disagree — and it is a LINK rather than a
          second piece of state, which is what makes that true. */}

      <div className={styles.toolbar}>
        <Input
          className={styles.search}
          // `type="search"` so the control announces itself as a search box
          // rather than a generic text field.
          type="search"
          value={searchQuery}
          onChange={(_, data) => setParam('q', data.value)}
          // No device name is projected onto the wire, so the mockup's "by serial
          // number, user, or device name" is not a promise this box can keep.
          placeholder="Search devices by serial number…"
          aria-label="Search devices by serial number"
          contentBefore={<Search size={16} strokeWidth={1.5} />}
        />

        {/* FD-372 — the lifecycle filter. In the URL (`?status=`), never in
            component state, so the filtered view is the shareable view. */}
        <Dropdown
          className={styles.filter}
          aria-label="Filter by lifecycle status"
          value={activeStatusFilterLabel}
          selectedOptions={[statusFilter ?? ALL_STATUSES_VALUE]}
          onOptionSelect={(_, data) => {
            const next = data.optionValue;
            setParam('status', !next || next === ALL_STATUSES_VALUE ? null : next);
          }}
        >
          <Option value={ALL_STATUSES_VALUE} text="All statuses">
            All statuses
          </Option>
          {LIFECYCLE_STATUS_FILTERS.map((f) => (
            <Option key={f.key} value={f.key} text={f.label}>
              {f.label}
            </Option>
          ))}
        </Dropdown>

        <Dropdown
          className={styles.filter}
          aria-label="Filter by readiness"
          value={activeReadinessFilterLabel}
          selectedOptions={[readinessFilter ?? ALL_READINESS_VALUE]}
          onOptionSelect={(_, data) => {
            const next = data.optionValue;
            setParam('held', !next || next === ALL_READINESS_VALUE ? null : next);
          }}
        >
          <Option value={ALL_READINESS_VALUE} text="All readiness">
            All readiness
          </Option>
          {READINESS_FILTERS.map((f) => (
            <Option key={f.key} value={f.key} text={f.label}>
              {f.label}
            </Option>
          ))}
        </Dropdown>

        {hasIdentityData && (
          <Dropdown
            className={styles.filter}
            aria-label="Filter by identity"
            value={activeIdentityFilterLabel}
            selectedOptions={[identityFilter ?? ALL_IDENTITIES_VALUE]}
            onOptionSelect={(_, data) => {
              const next = data.optionValue;
              setParam('identity', !next || next === ALL_IDENTITIES_VALUE ? null : next);
            }}
          >
            <Option value={ALL_IDENTITIES_VALUE} text="All identities">
              All identities
            </Option>
            {IDENTITY_FILTERS.map((f) => (
              <Option key={f.key} value={f.key} text={f.label}>
                {f.label}
              </Option>
            ))}
          </Dropdown>
        )}

        <Button
          appearance="primary"
          icon={<Plus size={16} strokeWidth={1.5} />}
          onClick={() => setAssignOpen(true)}
        >
          Assign devices
        </Button>
      </div>

      {/* The bulk bar. THREE ACTIONS, AND NONE OF THEM IS GATED ON WAVE STATE
          (R-FD-051 D1). The handoff asserts twice that an activated wave locks
          membership; the platform deliberately does the opposite in three ratified
          decisions — `manager/add-devices` states in terms that it has no
          `BprtStatus` guard, its active-wave branch exists to TOP UP the licence
          reservation (FD-ADR-041 §5), and FD-ADR-035 §3 makes remove+add a failed
          device's only retry path. Disabling these while a wave runs would remove
          the retry path and break the smoke suite's ballast device.

          They stay rendered-and-disabled rather than hidden until a selection
          exists: an operator has to be able to see that the actions are there
          before working out how to reach them. */}
      <div className={styles.selectionBar}>
        <span className="text-[13px]" style={{ color: TEXT.secondary }}>
          {selectedCount === 0 ? (
            'Select devices to move, remove, or override.'
          ) : (
            <>
              <span style={{ ...NUMERIC, color: TEXT.primary, fontWeight: 600 }}>
                {selectedCount.toLocaleString()}
              </span>{' '}
              selected
            </>
          )}
        </span>

        <Button
          appearance="subtle"
          icon={<ArrowRightLeft size={16} strokeWidth={1.5} />}
          disabled={selectedCount === 0}
          onClick={() => setMoveOpen(true)}
        >
          {/* The mockup labels this "Move to migration group". It is wrong:
              `MoveDevicesDialog` moves devices to another CAMPAIGN, which is
              FD-ADR-035 §3's supplementary-wave path. A group is an inert
              organisational container (FD-ADR-030 §3) and nothing moves into one
              from here. */}
          {selectedCount === 0 ? 'Move to migration…' : `Move ${selectedCount} to migration…`}
        </Button>
        <Button
          appearance="subtle"
          icon={<Trash2 size={16} strokeWidth={1.5} />}
          disabled={selectedCount === 0}
          onClick={() => setRemoveOpen(true)}
        >
          {selectedCount === 0
            ? 'Remove devices'
            : `Remove ${selectedCount} device${selectedCount === 1 ? '' : 's'}`}
        </Button>
        <Button
          appearance="subtle"
          icon={<ShieldAlert size={16} strokeWidth={1.5} />}
          disabled={selectedCount === 0}
          onClick={() => setOverrideOpen(true)}
        >
          {selectedCount === 0 ? 'Override readiness' : `Override readiness (${selectedCount})`}
        </Button>

        {selectedCount > 0 && (
          <Button
            appearance="subtle"
            icon={<X size={16} strokeWidth={1.5} />}
            onClick={() => setSelectedSerials(new Set())}
          >
            Clear selection
          </Button>
        )}

        {filtered && (
          // Said out loud, because a filtered grid and a short campaign look
          // identical. No "of loaded pages" caveat: the campaign's device list
          // arrives whole in one partition read, so this filter is complete.
          <span className="ml-auto text-[12px]" style={{ color: TEXT.secondary }}>
            Showing <span style={NUMERIC}>{visibleDevices.length.toLocaleString()}</span> of{' '}
            <span style={NUMERIC}>{devices.length.toLocaleString()}</span> devices
          </span>
        )}
      </div>

      <div className={styles.tableSurface}>
        <DeviceGrid
          devices={orderedDevices}
          onDeviceClick={handleDeviceClick}
          mappingColumn={mappingColumn}
          readinessColumn={{ enforcement: migration.readinessEnforcement ?? 'observe' }}
          // FD-494/FD-503 — "have we heard from it, and how recently", in one
          // column: the last-contact time with the check-in state beneath it.
          checkInColumn
          // FD-498 — where in the migration each device is, from its own
          // `lastActionId`. Gated on the field arriving (FD-497).
          stepColumn={hasStepData}
          // FD-503 — marks the canary row. The ORDER is this page's job
          // (`pinCanaryFirst` above); the grid renders what it is handed.
          canarySerial={migration.canarySerial}
          rowActions={rowActions}
          selection={{
            selectedKeys: selectedSerials,
            onSelectionChange: setSelectedSerials,
            keyForSelection: (d) => d.serialNumber,
          }}
          emptyState={
            devices.length === 0 ? (
              <MessageBar intent="info">
                <MessageBarBody>
                  <MessageBarTitle>No devices assigned yet</MessageBarTitle>
                  Assign device serial numbers to start tracking migration progress.
                </MessageBarBody>
                <MessageBarActions>
                  <Button
                    size="small"
                    appearance="primary"
                    icon={<Plus size={14} strokeWidth={1.5} />}
                    onClick={() => setAssignOpen(true)}
                  >
                    Assign devices
                  </Button>
                </MessageBarActions>
              </MessageBar>
            ) : (
              // A campaign WITH devices whose filters match none of them is a
              // different situation from an empty campaign, and offering "Assign
              // devices" there answers a question nobody asked.
              <MessageBar intent="info">
                <MessageBarBody>
                  <MessageBarTitle>No devices match these filters</MessageBarTitle>
                  {devices.length.toLocaleString()} device
                  {devices.length === 1 ? ' is' : 's are'} assigned to this campaign.
                </MessageBarBody>
                <MessageBarActions>
                  <Button
                    size="small"
                    onClick={() => {
                      const next = new URLSearchParams(searchParams);
                      for (const name of ['status', 'held', 'identity', 'q']) next.delete(name);
                      setSearchParams(next, { replace: true });
                    }}
                  >
                    Clear filters
                  </Button>
                </MessageBarActions>
              </MessageBar>
            )
          }
        />
      </div>

      {/* Below the grid: a per-wave reference aggregate, not a filter. It renders
          nothing until the broker has resolved at least one device. */}
      <AutopilotTagSummaryPanel migration={migration} />

      <AssignDevicesDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        migrationId={migrationId}
        migrationType={migration.migrationType}
        onDevicesAssigned={invalidateMigrationQueries}
      />
      {/* FD-503 — ONE SUBJECT, computed once: the row the `···` targeted, or the
          bulk selection. Each `onOpenChange` clears `rowTarget` as it closes, so
          the next bulk action cannot inherit a row's target. */}
      <ReadinessOverrideDialog
        open={overrideOpen}
        onOpenChange={(open) => {
          setOverrideOpen(open);
          if (!open) setRowTarget(null);
        }}
        migrationId={migrationId}
        selectedDevices={dialogDevices}
        onApplied={() => {
          invalidateMigrationQueries();
          setSelectedSerials(new Set());
          setRowTarget(null);
        }}
      />
      <MoveDevicesDialog
        open={moveOpen}
        onOpenChange={(open) => {
          setMoveOpen(open);
          if (!open) setRowTarget(null);
        }}
        migrationId={migrationId}
        migrationName={migration.migrationName}
        selectedDevices={dialogDevices}
        onMoved={() => {
          invalidateMigrationQueries();
          setSelectedSerials(new Set());
          setRowTarget(null);
        }}
      />
      <ConfirmRemoveDevicesModal
        open={removeOpen}
        onOpenChange={(open) => {
          setRemoveOpen(open);
          if (!open) setRowTarget(null);
        }}
        migrationId={migrationId}
        migrationName={migration.migrationName}
        selectedDevices={dialogDevices}
        onDevicesRemoved={() => {
          invalidateMigrationQueries();
          setSelectedSerials(new Set());
          setRowTarget(null);
        }}
      />
    </div>
  );
}
